import { FC, memo, useEffect, useRef } from 'react'

import { useRouterState } from '@tanstack/react-router'
import { LeaveConfirmation } from 'ui/components/LeaveConfirmation'
import { Portal } from 'ui/components/Portal'

import { useUploadProgress } from '../../hooks/useUploadProgress'
import { useUploadProgressWidget } from '../../hooks/useUploadProgressWidget'
import { UploadFileItem } from './components/UploadFileItem'
import { WidgetHeader } from './components/WidgetHeader'

import { s } from './styles'

const toastContainerSelector = '.Toastify__toast-container--bottom-right'

export const UploadProgressWidget: FC = memo(() => {
  const {
    uploadsArray,
    expanded,
    displayedProgress,
    activeCount,
    totalCount,
    hasErrorsOnly,
    showSuccessState,
    showCancelledState,
    isFadingOut,
    instantReset,
    handleToggle,
    handleCancelAll,
    handleClose
  } = useUploadProgressWidget()

  const { activeUploads, allCompleted, clearAllUploads } = useUploadProgress()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const prevPathnameRef = useRef(pathname)
  const widgetRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const pathnameChanged = prevPathnameRef.current !== pathname
    const allUploadsCompleted = allCompleted && activeUploads.length === 0

    if (pathnameChanged && allUploadsCompleted) {
      clearAllUploads()
    }

    prevPathnameRef.current = pathname
  }, [pathname, allCompleted, activeUploads.length, clearAllUploads])

  const handleLeave = () => {
    if (allCompleted && activeUploads.length === 0) {
      clearAllUploads()
    }
  }

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const root = document.documentElement
    const toastOffsetVar = '--toast-offset'
    const resetOffset = () => {
      root.style.removeProperty(toastOffsetVar)
      const toastContainers = document.querySelectorAll(toastContainerSelector)
      toastContainers.forEach((container) => {
        ;(container as HTMLElement).style.setProperty('bottom', '1em', 'important')
      })
    }

    const element = widgetRef.current

    if (!element) {
      resetOffset()
      return
    }

    const updateOffset = () => {
      const height = element.getBoundingClientRect().height
      const widgetBottomGap = 40

      if (height === 0) {
        resetOffset()
        return
      }

      const offset = height + widgetBottomGap + 16

      root.style.setProperty(toastOffsetVar, `${offset}px`)

      const toastContainers = document.querySelectorAll(toastContainerSelector)

      toastContainers.forEach((container) => {
        const htmlContainer = container as HTMLElement
        htmlContainer.style.setProperty('bottom', `${offset}px`, 'important')
      })
    }

    requestAnimationFrame(() => {
      updateOffset()
    })

    const timeoutId = setTimeout(() => {
      updateOffset()
    }, 750)

    const mutationObserver = new window.MutationObserver((mutations) => {
      const hasToastChanges = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some((node) => {
          return (
            node instanceof HTMLElement &&
            (node.classList.contains('Toastify__toast-container') || node.querySelector(toastContainerSelector))
          )
        })
      })

      if (hasToastChanges) {
        updateOffset()
      }
    })

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    })

    if ('ResizeObserver' in window) {
      const resizeObserver = new window.ResizeObserver(() => {
        updateOffset()
      })

      resizeObserver.observe(element)

      return () => {
        resizeObserver.disconnect()
        mutationObserver.disconnect()
        clearTimeout(timeoutId)
        resetOffset()
      }
    }

    if (typeof window !== 'undefined') (window as Window).addEventListener('resize', updateOffset)

    return () => {
      window.removeEventListener('resize', updateOffset)
      mutationObserver.disconnect()
      clearTimeout(timeoutId)
      resetOffset()
    }
  }, [uploadsArray.length, expanded])

  if (uploadsArray.length === 0) {
    return null
  }

  return (
    <LeaveConfirmation preventLeaving={activeUploads.length > 0} onLeave={handleLeave}>
      <Portal id="upload-progress-widget">
        <s.WidgetContainer ref={widgetRef} $expanded={expanded}>
          <WidgetHeader
            expanded={expanded}
            displayedProgress={displayedProgress}
            hasErrorsOnly={hasErrorsOnly}
            showSuccessState={showSuccessState}
            showCancelledState={showCancelledState}
            isFadingOut={isFadingOut}
            instantReset={instantReset}
            activeCount={activeCount}
            totalCount={totalCount}
            onToggle={handleToggle}
            onCancelAll={handleCancelAll}
            onClose={handleClose}
          />

          {expanded && (
            <>
              <s.FileList>
                {uploadsArray.length === 0 ? (
                  <s.EmptyState>No active uploads</s.EmptyState>
                ) : (
                  uploadsArray.map((upload) => <UploadFileItem key={upload.id} upload={upload} />)
                )}
              </s.FileList>
              {/* <WidgetFooter onAddFiles={handleAddFiles} /> */}
            </>
          )}
        </s.WidgetContainer>
      </Portal>
    </LeaveConfirmation>
  )
})
